import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp,
  Building2,
  Handshake,
  IndianRupee,
  Clock,
  ShieldCheck,
  Rocket,
  Users,
  Megaphone,
  HeadsetIcon,
  Settings,
  Target,
  BarChart3,
  CheckCircle2,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  MapPin,
  Zap,
  Award,
  Globe,
  Phone,
  Mail,
  AlertTriangle,
  Layers,
  Menu,
  X
} from 'lucide-react';

const WHATSAPP_URL = 'https://wa.me/919445875911?text=Hi%20doorush%2C%20I%27m%20interested%20in%20your%20franchise%20opportunity.%20Please%20share%20more%20details.';

const Franchise = () => {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const franchiseModels = [
    {
      title: 'Model A: Fully Company Operated (COCO)',
      badge: 'Passive Income',
      bestFor: 'Passive Investors / HNIs',
      howItWorks: [
        'doorush runs 100% of city operations',
        'Partner acts as capital partner only',
        'No staff, no customers, no daily ops'
      ],
      economics: {
        revenueShare: '10 - 20',
        payback: '12-24 months',
        effort: 'Very Low'
      },
      highlight: false
    },
    {
      title: 'Model B: 90% Company Operated (Hybrid COCO)',
      badge: 'Higher Returns',
      bestFor: 'Local Entrepreneurs / Operators',
      howItWorks: [
        'doorush controls app, pricing & demand',
        'Partner manages local execution & SLAs',
        'Higher involvement = higher upside'
      ],
      economics: {
        revenueShare: '20 - 30',
        payback: '10-24 months',
        effort: 'Moderate'
      },
      highlight: true
    }
  ];

  const investmentDetails = [
    {
      icon: IndianRupee,
      title: 'Initial Investment',
      description: '6L - 50L total investment. Includes franchise fee, refundable security deposit, initial working capital, and city launch costs.'
    },
    {
      icon: Clock,
      title: 'Break-even Point',
      description: 'Fast break-even enabled by high order density and operational efficiency. Model A: 12-24 months. Model B: 10-24 months.'
    },
    {
      icon: BarChart3,
      title: 'Revenue Model',
      description: 'Franchise partners earn through per-order Revenue Sharing driven by high daily volumes and repeat usage.'
    },
    {
      icon: TrendingUp,
      title: 'Return on Investment',
      description: 'Annual earning potential of 3L - 30L, depending on franchise model. Designed for strong first-year returns with scalable upside.'
    }
  ];

  const operationsSupport = [
    {
      icon: Rocket,
      title: 'Initial City Setup & Launch Support',
      description: 'doorush leads the city launch with partner onboarding, training, SOP rollout, and system setup. The franchise partner supports local coordination, basic logistics, and community access during launch.'
    },
    {
      icon: Settings,
      title: 'On-Ground Support from Franchise Partner',
      description: 'Franchise partner ensures local availability, assists with partner coordination, attendance tracking, and first-level issue handling (as per chosen franchise model).'
    },
    {
      icon: HeadsetIcon,
      title: 'Ongoing Operational & Central Support',
      description: 'doorush manages technology, demand generation, payments, customer support, quality audits, and escalations, ensuring smooth daily operations and SLA compliance.'
    }
  ];

  const marketingStrategy = [
    {
      icon: Award,
      title: 'Brand Positioning & Identity',
      description: 'doorush builds and maintains a consistent city-level brand with clear messaging around trust, speed, and reliability. All creatives, guidelines, and brand assets are centrally managed.'
    },
    {
      icon: Megaphone,
      title: 'Multi-Channel Marketing Campaigns',
      description: 'Centralized digital campaigns across search, social, and performance channels drive demand. Franchise partners are supported with localized creatives for community-level outreach.'
    },
    {
      icon: Users,
      title: 'Customer Acquisition & Retention',
      description: 'Structured referral programs, first-booking offers, repeat discounts, and subscription nudges improve acquisition and long-term retention across neighborhoods.'
    },
    {
      icon: Handshake,
      title: 'Partnerships & Collaboration',
      description: 'doorush enables tie-ups with gated communities, RWAs, and local businesses to unlock bulk demand, exclusive access, and cost-efficient customer acquisition.'
    }
  ];

  const marketStats = [
    { value: '$13.6B', label: 'Cleaning Services Market (2024)' },
    { value: '$0.13B', label: 'On-Demand Digital Slice (2024)' },
    { value: '100x', label: 'Offline to Digital Runway' }
  ];

  const risks = [
    'Workforce availability and attendance variability',
    'SLA compliance in high-demand periods',
    'Service consistency across regions'
  ];

  const mitigations = [
    'Centralized partner onboarding & backup pools',
    'Strict SOPs and performance tracking',
    'Franchise-level oversight and quality audits'
  ];

  const opportunities = [
    'Rising urban demand for on-demand maid & cook services',
    'Low market organization creates digital runway',
    'Strong repeat usage ensures predictable revenue',
    'Rapid city rollout across Tier 1, 2, and 3 markets',
    'Category expansion potential (new service verticals)'
  ];

  const faqs = [
    {
      question: 'What is the minimum investment required?',
      answer: 'The total investment ranges from 5 Lakhs to 50 Lakhs, which includes the franchise fee, a refundable security deposit, initial working capital, and city launch costs. The exact amount depends on the franchise model and city tier you choose.'
    },
    {
      question: 'Do I need to manage daily operations?',
      answer: 'It depends on your chosen model. Model A (COCO) requires zero operational involvement - doorush handles everything. Model B (Hybrid) requires moderate involvement in local execution and SLA management, but doorush still handles the platform, pricing, and demand generation.'
    },
    {
      question: 'How long does it take to break even?',
      answer: 'Model A typically breaks even in 12-24 months, while Model B can break even in 10-24 months due to higher per-order revenue sharing. Break-even timelines are accelerated by high order density and strong repeat usage patterns.'
    },
    {
      question: 'What support does doorush provide?',
      answer: 'doorush provides end-to-end support including technology platform, demand generation, payment processing, customer support, quality audits, SOP rollout, training, and marketing campaigns. You get a fully operational business backed by a proven platform.'
    },
    {
      question: 'Can I operate in any city?',
      answer: 'doorush is expanding across Tier 1, 2, and 3 cities in India. City availability depends on market readiness and demand potential. Contact us to check availability for your preferred city.'
    },
    {
      question: 'What is the franchise agreement duration?',
      answer: 'Franchise agreements are structured for long-term partnership with clear terms around territory, revenue sharing, and renewal options. Specific terms are discussed during the onboarding process.'
    }
  ];

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
              <img
                src="https://customer-assets.emergentagent.com/job_ec527428-3aec-42a4-a1f7-d2e66c0be0df/artifacts/hktez0bk_logo_transs.png"
                alt="doorush logo"
                className="h-10 w-auto"
              />
            </div>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center space-x-6">
              <button onClick={() => scrollToSection('models')} className="text-gray-600 hover:text-[#007e7a] transition-colors font-medium">Models</button>
              <button onClick={() => scrollToSection('investment')} className="text-gray-600 hover:text-[#007e7a] transition-colors font-medium">Investment</button>
              <button onClick={() => scrollToSection('support')} className="text-gray-600 hover:text-[#007e7a] transition-colors font-medium">Support</button>
              <button onClick={() => scrollToSection('faq')} className="text-gray-600 hover:text-[#007e7a] transition-colors font-medium">FAQ</button>
              <Button onClick={() => navigate('/')} variant="outline" className="border-2 border-[#007e7a] text-[#007e7a] hover:bg-[#007e7a] hover:text-white">
                Back to Home
              </Button>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#007e7a] hover:bg-[#006666] text-white">
                  Get Started
                </Button>
              </a>
            </nav>

            {/* Mobile Menu Button */}
            <button className="md:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden pb-4 space-y-2">
              <button onClick={() => scrollToSection('models')} className="block w-full text-left px-4 py-2 text-gray-600 hover:text-[#007e7a] hover:bg-gray-50 rounded-lg">Models</button>
              <button onClick={() => scrollToSection('investment')} className="block w-full text-left px-4 py-2 text-gray-600 hover:text-[#007e7a] hover:bg-gray-50 rounded-lg">Investment</button>
              <button onClick={() => scrollToSection('support')} className="block w-full text-left px-4 py-2 text-gray-600 hover:text-[#007e7a] hover:bg-gray-50 rounded-lg">Support</button>
              <button onClick={() => scrollToSection('faq')} className="block w-full text-left px-4 py-2 text-gray-600 hover:text-[#007e7a] hover:bg-gray-50 rounded-lg">FAQ</button>
              <div className="flex gap-2 px-4 pt-2">
                <Button onClick={() => { navigate('/'); setMobileMenuOpen(false); }} variant="outline" className="flex-1 border-2 border-[#007e7a] text-[#007e7a]">Home</Button>
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="flex-1">
                  <Button className="w-full bg-[#007e7a] text-white">Get Started</Button>
                </a>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#007e7a] to-[#005555] text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>
        <div className="max-w-7xl mx-auto relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="bg-white/20 text-white border-white/30 mb-6 text-sm px-4 py-1">
                Expansion &bull; Partnership &bull; Growth
              </Badge>
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6 leading-tight">
                doorush Business <span className="text-orange-300">Franchise</span>
              </h1>
              <p className="text-lg md:text-xl opacity-90 mb-8 leading-relaxed max-w-xl">
                Partner with India's fastest-growing on-demand home services platform. Choose between passive returns or operator upside — doorush handles the platform, demand, and scale.
              </p>
              <div className="flex flex-wrap gap-4">
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                  <Button
                    size="lg"
                    className="bg-white text-[#007e7a] hover:bg-gray-100 text-lg px-8 py-6 font-semibold"
                  >
                    Become a Partner <ArrowRight className="ml-2" size={20} />
                  </Button>
                </a>
                <Button
                  onClick={() => scrollToSection('models')}
                  size="lg"
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white/10 text-lg px-8 py-6"
                >
                  Explore Models
                </Button>
              </div>
              <div className="grid grid-cols-3 gap-6 mt-12 pt-8 border-t border-white/20">
                <div>
                  <div className="text-3xl font-bold">6L - 50L</div>
                  <div className="text-sm opacity-75 mt-1">Investment Based on Location</div>
                </div>
                <div>
                  <div className="text-3xl font-bold">10-24</div>
                  <div className="text-sm opacity-75 mt-1">Months Payback</div>
                </div>
                <div>
                  <div className="text-3xl font-bold">3L - 30L</div>
                  <div className="text-sm opacity-75 mt-1">Annual Returns</div>
                </div>
              </div>
            </div>
            <div className="hidden lg:flex justify-center">
              <div className="relative">
                <div className="w-80 h-80 bg-white/10 rounded-3xl flex items-center justify-center backdrop-blur-sm border border-white/20">
                  <div className="text-center p-8">
                    <Building2 size={64} className="mx-auto mb-4 opacity-90" />
                    <p className="text-2xl font-bold mb-2">Own a doorush</p>
                    <p className="text-lg opacity-80">City Franchise</p>
                    <div className="mt-4 flex items-center justify-center gap-2 text-orange-300">
                      <Zap size={16} />
                      <span className="text-sm font-medium">Low Risk, High Growth</span>
                    </div>
                  </div>
                </div>
                <div className="absolute -top-6 -right-6 w-24 h-24 bg-orange-400/20 rounded-2xl flex items-center justify-center">
                  <TrendingUp size={32} className="text-orange-300" />
                </div>
                <div className="absolute -bottom-6 -left-6 w-24 h-24 bg-white/10 rounded-2xl flex items-center justify-center">
                  <IndianRupee size={32} className="text-white/80" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <Badge className="bg-[#007e7a]/10 text-[#007e7a] border-[#007e7a]/20 mb-6">About doorush</Badge>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Making Home Services Fast, Reliable & Stress-Free
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed mb-8">
            doorush was born to make home services fast, reliable, and stress-free. Finding trustworthy maids, cooks, or other household help can be time-consuming — but with doorush, help is just a few taps away.
          </p>
          <p className="text-lg text-gray-600 leading-relaxed">
            Founded by <strong>Raghulan Gowthaman</strong>, <strong>Sudharsan Ramesh</strong> & <strong>Praison Cherub</strong>, doorush connects users with verified professionals for instant home services, while creating flexible earning opportunities for service providers.
          </p>
        </div>
      </section>

      {/* Market Analysis */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-[#007e7a]/10 text-[#007e7a] border-[#007e7a]/20 mb-4">Market Opportunity</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Market Analysis</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              India's paid domestic help remains a large, under-organized category with fast-growing digital demand. The organised cleaning & maid segment is worth billions while the online/on-demand slice is nascent — creating a wide offline to digital runway.
            </p>
          </div>

          {/* Market Stats */}
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {marketStats.map((stat, index) => (
              <Card key={index} className="p-6 text-center hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border-2 border-transparent hover:border-[#007e7a]/20">
                <CardContent className="pt-6">
                  <div className="text-4xl md:text-5xl font-bold text-[#007e7a] mb-2">{stat.value}</div>
                  <div className="text-gray-600">{stat.label}</div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Market Details */}
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="p-6 bg-gradient-to-br from-[#007e7a]/5 to-white border-2 border-[#007e7a]/10">
              <CardContent className="pt-6">
                <div className="w-12 h-12 bg-[#007e7a]/10 rounded-full flex items-center justify-center mb-4">
                  <Target className="text-[#007e7a]" size={24} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Target Customer Segments</h3>
                <p className="text-gray-600">Urban dual-income families and gated community residents needing reliable, verified maid and cook services with high repeat, short-duration demand.</p>
              </CardContent>
            </Card>
            <Card className="p-6 bg-gradient-to-br from-[#007e7a]/5 to-white border-2 border-[#007e7a]/10">
              <CardContent className="pt-6">
                <div className="w-12 h-12 bg-[#007e7a]/10 rounded-full flex items-center justify-center mb-4">
                  <BarChart3 className="text-[#007e7a]" size={24} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Competitive Landscape</h3>
                <p className="text-gray-600">Highly fragmented market dominated by informal agents and a few national platforms, creating gaps in speed, consistency, and hyperlocal availability.</p>
              </CardContent>
            </Card>
            <Card className="p-6 bg-gradient-to-br from-[#007e7a]/5 to-white border-2 border-[#007e7a]/10">
              <CardContent className="pt-6">
                <div className="w-12 h-12 bg-[#007e7a]/10 rounded-full flex items-center justify-center mb-4">
                  <TrendingUp className="text-[#007e7a]" size={24} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Market Trends</h3>
                <p className="text-gray-600">Shift toward app-based, on-demand maid and cook bookings with emphasis on verification, hourly services, and flexible subscription pricing.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Franchise Models */}
      <section id="models" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-[#007e7a]/10 text-[#007e7a] border-[#007e7a]/20 mb-4">Choose Your Path</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Franchise Models</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Choose between passive returns or operator upside — doorush handles the platform, demand, and scale.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {franchiseModels.map((model, index) => (
              <Card
                key={index}
                className={`p-8 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 relative overflow-hidden ${
                  model.highlight
                    ? 'border-2 border-[#007e7a] ring-4 ring-[#007e7a]/10'
                    : 'border-2 border-gray-200'
                }`}
              >
                {model.highlight && (
                  <div className="absolute top-0 right-0 bg-[#007e7a] text-white text-xs font-bold px-4 py-1 rounded-bl-lg">
                    RECOMMENDED
                  </div>
                )}
                <CardContent className="pt-4">
                  <Badge className={`mb-4 ${model.highlight ? 'bg-[#007e7a] text-white' : 'bg-[#007e7a]/10 text-[#007e7a]'}`}>
                    {model.badge}
                  </Badge>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{model.title}</h3>
                  <p className="text-[#007e7a] font-semibold mb-6">Best for: {model.bestFor}</p>

                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-3">How it works</h4>
                    <ul className="space-y-2">
                      {model.howItWorks.map((item, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <CheckCircle2 className="text-[#007e7a] mt-0.5 flex-shrink-0" size={18} />
                          <span className="text-gray-600">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="bg-gray-50 rounded-2xl p-5">
                    <h4 className="font-semibold text-gray-900 mb-3">Economics</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Revenue Sharing</span>
                        <span className="font-bold text-[#007e7a] text-lg">{model.economics.revenueShare} / order</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Payback Period</span>
                        <span className="font-semibold text-gray-900">{model.economics.payback}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Effort Required</span>
                        <Badge className={`${model.economics.effort === 'Very Low' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                          {model.economics.effort}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Investment & Financials */}
      <section id="investment" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#007e7a]/5 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-[#007e7a]/10 text-[#007e7a] border-[#007e7a]/20 mb-4">Financials</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Investment & Returns</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Designed to deliver predictable cash flows, fast payback, and low operational risk. Partners benefit from high-frequency transactions and clear unit economics.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {investmentDetails.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <Card key={index} className="p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                  <CardContent className="pt-6">
                    <div className="w-14 h-14 bg-[#007e7a]/10 rounded-2xl flex items-center justify-center mb-5">
                      <IconComponent className="text-[#007e7a]" size={28} />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{item.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Operations & Support */}
      <section id="support" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-[#007e7a]/10 text-[#007e7a] border-[#007e7a]/20 mb-4">We've Got You Covered</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Operations & Support</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {operationsSupport.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <Card key={index} className="p-8 bg-gradient-to-br from-[#007e7a] to-[#005555] text-white hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                  <CardContent className="pt-4">
                    <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center mb-5">
                      <IconComponent className="text-white" size={28} />
                    </div>
                    <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                    <p className="opacity-90 leading-relaxed">{item.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Marketing & Brand Strategy */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-[#007e7a]/10 text-[#007e7a] border-[#007e7a]/20 mb-4">Growth Engine</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Marketing & Brand Strategy</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {marketingStrategy.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <Card key={index} className="p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                  <CardContent className="pt-6">
                    <div className="w-12 h-12 bg-[#007e7a]/10 rounded-full flex items-center justify-center mb-4">
                      <IconComponent className="text-[#007e7a]" size={24} />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-3">{item.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{item.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Risk & Opportunities */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-[#007e7a]/10 text-[#007e7a] border-[#007e7a]/20 mb-4">Risk & Opportunity</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Risk & Opportunities</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              doorush operates in a large but operationally complex home-services market. The platform-led and franchise-backed model significantly reduces risk and unlocks strong long-term scalability.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Risks */}
            <Card className="p-6 border-2 border-orange-200 bg-orange-50/50">
              <CardContent className="pt-4">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                  <AlertTriangle className="text-orange-600" size={24} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Potential Risks</h3>
                <ul className="space-y-3">
                  {risks.map((risk, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-orange-500 mt-2 flex-shrink-0"></div>
                      <span className="text-gray-600">{risk}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Mitigations */}
            <Card className="p-6 border-2 border-blue-200 bg-blue-50/50">
              <CardContent className="pt-4">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <ShieldCheck className="text-blue-600" size={24} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Risk Mitigations</h3>
                <ul className="space-y-3">
                  {mitigations.map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle2 className="text-blue-600 mt-0.5 flex-shrink-0" size={16} />
                      <span className="text-gray-600">{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Opportunities */}
            <Card className="p-6 border-2 border-[#007e7a]/20 bg-[#007e7a]/5">
              <CardContent className="pt-4">
                <div className="w-12 h-12 bg-[#007e7a]/10 rounded-full flex items-center justify-center mb-4">
                  <Rocket className="text-[#007e7a]" size={24} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Growth Opportunities</h3>
                <ul className="space-y-3">
                  {opportunities.map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle2 className="text-[#007e7a] mt-0.5 flex-shrink-0" size={16} />
                      <span className="text-gray-600">{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-[#007e7a]/10 text-[#007e7a] border-[#007e7a]/20 mb-4">Common Questions</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <Card
                key={index}
                className={`overflow-hidden transition-all duration-300 ${openFaq === index ? 'border-2 border-[#007e7a]/20 shadow-lg' : 'border border-gray-200'}`}
              >
                <button
                  className="w-full p-6 text-left flex justify-between items-center gap-4"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                >
                  <h3 className="text-lg font-semibold text-gray-900">{faq.question}</h3>
                  {openFaq === index ? (
                    <ChevronUp className="text-[#007e7a] flex-shrink-0" size={20} />
                  ) : (
                    <ChevronDown className="text-gray-400 flex-shrink-0" size={20} />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-6">
                    <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA / Contact Section */}
      <section id="contact" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-[#007e7a] to-[#005555] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready to Partner with doorush?</h2>
          <p className="text-xl mb-10 opacity-90 max-w-2xl mx-auto">
            Join India's fastest-growing home services franchise. Start your journey today with an investment as low as 5 Lakhs.
          </p>
          <div className="flex flex-wrap justify-center gap-6 mb-12">
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 bg-white/10 hover:bg-white/20 transition-colors rounded-2xl px-6 py-4 border border-white/20">
              <Phone size={24} />
              <div className="text-left">
                <div className="text-sm opacity-75">Call / WhatsApp</div>
                <div className="font-semibold">+91 94458 75911</div>
              </div>
            </a>
<a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 bg-white/10 hover:bg-white/20 transition-colors rounded-2xl px-6 py-4 border border-white/20">
              <Globe size={24} />
              <div className="text-left">
                <div className="text-sm opacity-75">WhatsApp</div>
                <div className="font-semibold">+91 94458 75911</div>
              </div>
            </a>
          </div>
          <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
            <Button
              size="lg"
              className="bg-white text-[#007e7a] hover:bg-gray-100 text-lg px-10 py-6 font-semibold"
            >
              Become a Partner <ArrowRight className="ml-2" size={20} />
            </Button>
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12 px-4 sm:px-6 lg:px-8 border-t border-gray-800">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <img
                src="https://customer-assets.emergentagent.com/job_ec527428-3aec-42a4-a1f7-d2e66c0be0df/artifacts/hktez0bk_logo_transs.png"
                alt="doorush"
                className="h-10 mb-4 brightness-200 cursor-pointer"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              />
              <p className="text-sm mb-4">
                Your trusted partner for instant home services. Quality help at your doorstep.
              </p>
            </div>

            <div>
              <h3 className="text-white font-semibold mb-4">Franchise</h3>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => scrollToSection('models')} className="hover:text-[#007e7a] transition-colors text-left">Franchise Models</button></li>
                <li><button onClick={() => scrollToSection('investment')} className="hover:text-[#007e7a] transition-colors text-left">Investment</button></li>
                <li><button onClick={() => scrollToSection('support')} className="hover:text-[#007e7a] transition-colors text-left">Operations & Support</button></li>
                <li><button onClick={() => scrollToSection('faq')} className="hover:text-[#007e7a] transition-colors text-left">FAQ</button></li>
              </ul>
            </div>

            <div>
              <h3 className="text-white font-semibold mb-4">Company</h3>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => navigate('/')} className="hover:text-[#007e7a] transition-colors text-left">Home</button></li>
                <li><button onClick={() => navigate('/about')} className="hover:text-[#007e7a] transition-colors text-left">About Us</button></li>
                <li><button onClick={() => navigate('/teams')} className="hover:text-[#007e7a] transition-colors text-left">Our Team</button></li>
                <li><button onClick={() => navigate('/contact')} className="hover:text-[#007e7a] transition-colors text-left">Contact Us</button></li>
              </ul>
            </div>

            <div>
              <h3 className="text-white font-semibold mb-4">Legal</h3>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => navigate('/privacy-policy')} className="hover:text-[#007e7a] transition-colors text-left">Privacy Policy</button></li>
                <li><button onClick={() => navigate('/terms-conditions')} className="hover:text-[#007e7a] transition-colors text-left">Terms & Conditions</button></li>
                <li><button onClick={() => navigate('/refund-policy')} className="hover:text-[#007e7a] transition-colors text-left">Refund Policy</button></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-700 pt-8 text-center text-sm">
            <p>&copy; 2025 doorush. All rights reserved. Made with care in Chennai</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Franchise;
